// [file name]: LoginPage.js (src/pages/RestaurantPOS/LoginPage.js)
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Typography,
  Button,
  Avatar,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Alert,
  AlertTitle,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import VerifiedIcon from '@mui/icons-material/Verified';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';

import backgroundImage from '../../assets/elegant.PNG';
import logo from '../../assets/elegantelogo.png';
import Preloader from '../../theme/Preloader';
import restaurantConfig from '../../config/Organisation_Details';
import { getRedirectPathByRole } from '../../utility/roleRedirect';

// Configure logger with all methods
const logger = {
  info: (message, data) => console.log(`[LOGIN] ${message}`, data || ''),
  error: (message, error) => console.error(`[LOGIN] ${message}`, error || ''),
  debug: (message, data) => console.debug(`[LOGIN] ${message}`, data || ''),
  warn: (message, data) => console.warn(`[LOGIN] ${message}`, data || ''),
};

// ============================================
// AUTHENTICATION SERVICE (merged)
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const API_PREFIX = process.env.REACT_APP_API_PREFIX || '/pos_v1';

// Full API URL construction
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;
logger.info('API Configuration', { API_BASE_URL, API_PREFIX, FULL_API_URL });

// Create axios instance
const api = axios.create({
  baseURL: FULL_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.debug('Token added to request', { url: config.url });
    }
    return config;
  },
  (error) => {
    logger.error('Request interceptor error', error);
    return Promise.reject(error);
  }
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => {
    logger.debug('Response received', { 
      url: response.config.url, 
      status: response.status 
    });
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Only attempt refresh on 401 and not already retrying
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const refreshToken = localStorage.getItem('refresh_token');
      logger.info('Token expired, attempting refresh');
      
      if (refreshToken) {
        try {
          const response = await axios.post(
            `${FULL_API_URL}/auth/refresh`,
            { refresh_token: refreshToken }
          );
          
          if (response.data.access_token) {
            localStorage.setItem('access_token', response.data.access_token);
            originalRequest.headers.Authorization = `Bearer ${response.data.access_token}`;
            logger.info('Token refreshed successfully');
            return api(originalRequest);
          }
        } catch (refreshError) {
          logger.error('Token refresh failed', refreshError);
          clearAuthData();
        }
      } else {
        logger.warn('No refresh token available');
        clearAuthData();
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth helper functions
const clearAuthData = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('user_email');
  localStorage.removeItem('user_role');
  localStorage.removeItem('user_id');
  localStorage.removeItem('user_name');
  localStorage.removeItem('first_name');
  localStorage.removeItem('staff_id');
  localStorage.removeItem('department');
  localStorage.removeItem('phone');
  localStorage.removeItem('gender');
  localStorage.removeItem('is_active');
  localStorage.removeItem('user_location');
  logger.info('Auth data cleared');
};

const storeUserData = (user) => {
  if (user) {
    localStorage.setItem('user_data', JSON.stringify(user));
    localStorage.setItem('user_email', user.email);
    localStorage.setItem('user_role', user.role);
    localStorage.setItem('user_id', user.id.toString());
    localStorage.setItem('user_name', `${user.first_name} ${user.other_names || ''}`.trim());
    localStorage.setItem('first_name', user.first_name);
    localStorage.setItem('staff_id', user.staff_id);
    localStorage.setItem('department', user.department);
    localStorage.setItem('phone', user.phone);
    localStorage.setItem('gender', user.gender);
    localStorage.setItem('is_active', user.is_active ? 'true' : 'false');
    localStorage.setItem('user_location', user.location || ''); // ✅ Store location
    logger.debug('User data stored', { 
      email: user.email, 
      role: user.role,
      location: user.location 
    });
  }
};

const loginUser = async (email, password) => {
  try {
    logger.info('Login attempt', { email });
    
    const response = await api.post('/auth/login-json', {
      email,
      password,
    });
    
    logger.debug('Login response received', { 
      status: response.status,
      hasUser: !!response.data.user 
    });
    
    if (response.data) {
      const { access_token, refresh_token, user } = response.data;
      
      // Store tokens
      localStorage.setItem('access_token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
      }
      
      // Store user data
      storeUserData(user);
      
      logger.info('Login successful', { 
        email: user.email, 
        role: user.role,
        location: user.location,
        name: `${user.first_name} ${user.other_names || ''}`.trim()
      });
      
      return {
        success: true,
        data: response.data,
        user: user,
      };
    }
    
    return {
      success: false,
      error: 'Invalid response from server',
    };
  } catch (error) {
    logger.error('Login failed', error);
    
    let errorMessage = 'Login failed. Please try again.';
    if (error.response) {
      const errorData = error.response.data;
      if (errorData.detail) {
        errorMessage = errorData.detail;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      } else if (errorData.error) {
        errorMessage = errorData.error;
      }
      
      if (error.response.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (error.response.status === 403) {
        errorMessage = 'Account is deactivated or you don\'t have permission';
      } else if (error.response.status === 422) {
        errorMessage = 'Validation error. Please check your input.';
      } else if (error.response.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      logger.error('Login error details', { 
        status: error.response.status, 
        data: error.response.data 
      });
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
      logger.error('No response received', { request: error.request });
    } else {
      errorMessage = error.message || 'An unexpected error occurred.';
      logger.error('Request setup error', { message: error.message });
    }
    
    return {
      success: false,
      error: errorMessage,
      details: error.response?.data,
    };
  }
};

const isAuthenticated = () => {
  const token = localStorage.getItem('access_token');
  const userData = localStorage.getItem('user_data');
  const isAuth = !!(token && userData);
  logger.debug('Authentication check', { isAuthenticated: isAuth });
  return isAuth;
};

const getUserRole = () => {
  return localStorage.getItem('user_role') || null;
};

const getUserLocation = () => {
  return localStorage.getItem('user_location') || '';
};

// ============================================
// LOGIN PAGE COMPONENT
// ============================================

const Restaurant_LoginPage = () => {
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({ email: '', password: '' });
  const [serverError, setServerError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  // Add ref to prevent double navigation
  const hasRedirected = useRef(false);

  const from = location.state?.from?.pathname || '/WaitersDashboard';

  // Get restaurant details from config
  const restaurantName = 'Restaurant/Lounge';
  const restaurantSlogan = restaurantConfig.slogan || 'Where Great Food and Service Meet';
  const restaurantPhone = restaurantConfig.phone || '+234 800 123 4567';
  const restaurantEmail = restaurantConfig.email || 'info@greenvelvet.com';
  const restaurantAddress = restaurantConfig.address || '123 Elegante Street, Lagos, Nigeria';
  const operatingHours = restaurantConfig.operatingHours || 'Mon-Sun: 8:00 AM - 10:00 PM';
  const supportEmail = restaurantConfig.supportEmail || 'support@greenvelvet.com';
  const supportPhone = restaurantConfig.supportPhone || '+234 800 765 4321';

  const primaryColor = '#2E7D32';
  const primaryLight = '#4CAF50';
  const secondaryColor = '#C62828';
  const secondaryDark = '#B71C1C';
  const whiteColor = '#FFFFFF';

  const greenGradient = `linear-gradient(135deg, ${primaryColor} 0%, ${primaryLight} 100%)`;
  const redGradient = `linear-gradient(135deg, ${secondaryColor} 0%, ${secondaryDark} 100%)`;

  // Check if already authenticated
  useEffect(() => {
    const checkAuth = async () => {
      // Prevent multiple redirects
      if (hasRedirected.current) return;
      
      if (isAuthenticated()) {
        logger.info('User already authenticated, redirecting');
        hasRedirected.current = true;
        const userRole = getUserRole();
        const userLocation = getUserLocation();
        const redirectPath = getRedirectPathByRole(userRole, userLocation);
        logger.info('Redirecting authenticated user', { role: userRole, location: userLocation, path: redirectPath });
        navigate(redirectPath, { replace: true });
        return;
      }
      setIsPageLoading(false);
    };
    
    checkAuth();
  }, [navigate]);

  const validate = () => {
    const newErrors = { email: '', password: '' };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setServerError('');
    setSuccessMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Prevent double submission
    if (isLoading) return;

    setIsLoading(true);
    setServerError('');
    setSuccessMessage('');

    try {
      logger.info('Login attempt', { email: formData.email });
      
      const result = await loginUser(formData.email, formData.password);
      
      if (result.success) {
        setSuccessMessage('Login successful!');
        setServerError('');

        const user = result.user;
        const role = user?.role || 'user';
        const location = user?.location || '';

        // Store user data in localStorage
        localStorage.setItem('email', user.email);
        localStorage.setItem('role', role);
        localStorage.setItem('userId', user.id.toString());
        localStorage.setItem('userName', `${user.first_name} ${user.other_names || ''}`.trim());
        localStorage.setItem('user_location', location); // ✅ Store location
        
        // Store additional user data
        if (user.staff_id) {
          localStorage.setItem('staffId', user.staff_id);
        }
        if (user.department) {
          localStorage.setItem('userDepartment', user.department);
        }
        if (user.phone) {
          localStorage.setItem('phoneNumber', user.phone);
        }

        // Use the imported redirect function with location
        const redirectPath = getRedirectPathByRole(role, location);
        logger.info('Redirecting to', { role, location, path: redirectPath });
        
        // Use replace to prevent going back to login
        // Small delay to ensure state updates are complete
        setTimeout(() => {
          navigate(redirectPath, { replace: true });
        }, 300);
        
      } else {
        setServerError(result.error || 'Login failed. Please try again.');
        logger.error('Login failed', { error: result.error });
        setIsLoading(false);
      }
    } catch (error) {
      logger.error('Login error', error);
      setServerError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const features = [
    { icon: <SecurityIcon />, title: 'Secure', description: 'Enterprise-grade security' },
    { icon: <SpeedIcon />, title: 'Fast', description: 'Lightning quick access' },
    { icon: <VerifiedIcon />, title: 'Verified', description: 'Trusted by thousands' },
  ];

  if (isPageLoading) {
    return <Preloader text={`Loading ${restaurantName}...`} size={70} fullScreen={true} />;
  }

  if (isLoading) {
    return <Preloader text="Signing you in..." size={70} fullScreen={true} />;
  }

  return (
    <Box
      sx={{
        height: '100vh',
        width: '100vw',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: `linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 100%), url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        padding: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: 'absolute',
          width: '100%',
          height: '100%',
          top: 0,
          left: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        {[...Array(20)].map((_, i) => (
          <Box
            key={i}
            sx={{
              position: 'absolute',
              width: Math.random() * 4 + 2,
              height: Math.random() * 4 + 2,
              background: `rgba(46, 125, 50, ${Math.random() * 0.2 + 0.05})`,
              borderRadius: '50%',
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `float ${Math.random() * 10 + 5}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              '@keyframes float': {
                '0%, 100%': {
                  transform: 'translate(0, 0) scale(1)',
                  opacity: 0.2,
                },
                '25%': {
                  transform: 'translate(20px, -30px) scale(1.5)',
                  opacity: 0.4,
                },
                '50%': {
                  transform: 'translate(-10px, -60px) scale(1)',
                  opacity: 0.6,
                },
                '75%': {
                  transform: 'translate(30px, -20px) scale(1.3)',
                  opacity: 0.3,
                },
              },
            }}
          />
        ))}
      </Box>

      <Grid
        container
        sx={{
          maxWidth: 1400,
          width: '100%',
          height: '100vh',
          position: 'relative',
          padding: { xs: 2, md: 4 },
        }}
      >
        {/* LEFT SIDE - Brand Section */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            padding: 6,
            position: 'relative',
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 2,
              maxWidth: 500,
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                mb: 2,
                p: 1,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.8) 100%)',
                backdropFilter: 'blur(15px)',
                borderRadius: '16px',
                border: '1px solid rgba(198, 40, 40, 0.15)',
                boxShadow: `
                  0 8px 32px rgba(198, 40, 40, 0.15),
                  0 2px 8px rgba(0,0,0,0.08),
                  inset 0 1px 0 rgba(255,255,255,0.6)
                `,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt={restaurantName}
                sx={{
                  width: 90,
                  height: 'auto',
                  filter: `
                    drop-shadow(0 4px 24px rgba(198, 40, 40, 0.3))
                    drop-shadow(0 0 60px rgba(198, 40, 40, 0.1))
                    brightness(1.05) 
                    contrast(1.05)
                  `,
                  animation: 'pulseGlow 3s ease-in-out infinite, subtleFloat 4s ease-in-out infinite',
                  position: 'relative',
                  zIndex: 1,
                  display: 'block',
                  borderRadius: '10px',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    filter: `
                      drop-shadow(0 6px 32px rgba(198, 40, 40, 0.4))
                      drop-shadow(0 0 80px rgba(198, 40, 40, 0.15))
                      brightness(1.1) 
                      contrast(1.1)
                    `,
                  },
                  '@keyframes pulseGlow': {
                    '0%, 100%': {
                      filter: `
                        drop-shadow(0 4px 24px rgba(198, 40, 40, 0.3))
                        drop-shadow(0 0 40px rgba(198, 40, 40, 0.1))
                        brightness(1.05) 
                        contrast(1.05)
                      `
                    },
                    '50%': {
                      filter: `
                        drop-shadow(0 4px 40px rgba(198, 40, 40, 0.5))
                        drop-shadow(0 0 80px rgba(198, 40, 40, 0.2))
                        brightness(1.1) 
                        contrast(1.1)
                      `
                    },
                  },
                  '@keyframes subtleFloat': {
                    '0%, 100%': {
                      transform: 'translateY(0px)',
                    },
                    '50%': {
                      transform: 'translateY(-4px)',
                    },
                  },
                }}
              />
            </Box>
            
          <Typography
  variant="h2"
  sx={{
    color: whiteColor,
    fontWeight: 700,
    fontSize: '2.5rem', // Reduced from 3.5rem
    lineHeight: 1.2,
    mb: 2,
    textShadow: '0 4px 30px rgba(0,0,0,0.3)',
    '& span': {
      background: greenGradient,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      display: 'inline-block',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      borderRight: `3px solid ${primaryColor}`,
      animation: 'typing 3.5s steps(30, end) forwards, blink 0.75s step-end infinite',
      maxWidth: '0',
      animationFillMode: 'forwards',
    },
    '@keyframes typing': {
      '0%': { maxWidth: '0' },
      '100%': { maxWidth: '100%' },
    },
    '@keyframes blink': {
      '0%, 100%': { borderColor: 'transparent' },
      '50%': { borderColor: primaryColor },
    },
  }}
>
  <span>{restaurantName}</span>
</Typography>

            <Typography
              variant="body1"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontSize: '1.1rem',
                lineHeight: 1.8,
                mb: 4,
                maxWidth: 400,
              }}
            >
              {restaurantSlogan}
            </Typography>

            <Stack direction="row" spacing={2} sx={{ mb: 4 }}>
              {features.map((feature, index) => (
                <Chip
                  key={index}
                  icon={feature.icon}
                  label={feature.title}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.06)',
                    backdropFilter: 'blur(10px)',
                    color: whiteColor,
                    border: '1px solid rgba(255,255,255,0.08)',
                    '& .MuiChip-icon': {
                      color: primaryColor,
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255,255,255,0.12)',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease',
                    },
                  }}
                />
              ))}
            </Stack>

            <Box
              sx={{
                display: 'flex',
                gap: 4,
                borderTop: '1px solid rgba(255,255,255,0.05)',
                paddingTop: 3,
              }}
            >
              {[
                { value: '99.9%', label: 'Uptime' },
                { value: '24/7', label: 'IT Support' },
              ].map((stat, index) => (
                <Box key={index}>
                  <Typography
                    variant="h5"
                    sx={{
                      color: whiteColor,
                      fontWeight: 700,
                      fontSize: '1.5rem',
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'rgba(255,255,255,0.4)',
                      fontSize: '0.75rem',
                      letterSpacing: 1,
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.5,
                mt: 3,
                pt: 2,
                borderTop: '1px solid rgba(255,255,255,0.05)',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                }}
              >
                {restaurantAddress}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.3)',
                  fontSize: '0.7rem',
                  letterSpacing: 0.5,
                }}
              >
                {restaurantPhone} | {restaurantEmail}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  color: 'rgba(255,255,255,0.2)',
                  fontSize: '0.65rem',
                  letterSpacing: 0.5,
                }}
              >
                {operatingHours}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              position: 'absolute',
              top: '20%',
              right: '-10%',
              width: 400,
              height: 400,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${primaryColor}10 0%, transparent 70%)`,
              filter: 'blur(60px)',
              zIndex: 0,
              animation: 'orbit 20s linear infinite',
              '@keyframes orbit': {
                '0%': { transform: 'rotate(0deg) translateX(50px) rotate(0deg)' },
                '100%': { transform: 'rotate(360deg) translateX(50px) rotate(-360deg)' },
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: '10%',
              left: '-5%',
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: `radial-gradient(circle, ${primaryLight}10 0%, transparent 70%)`,
              filter: 'blur(60px)',
              zIndex: 0,
              animation: 'orbit 25s linear infinite reverse',
              '@keyframes orbit': {
                '0%': { transform: 'rotate(0deg) translateX(30px) rotate(0deg)' },
                '100%': { transform: 'rotate(-360deg) translateX(30px) rotate(360deg)' },
              },
            }}
          />
        </Grid>

        {/* RIGHT SIDE - Login Form */}
        <Grid
          item
          xs={12}
          md={5.5}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginLeft: { md: 'auto' },
            height: '100%',
          }}
        >
          <Box
            sx={{
              width: '100%',
              maxWidth: 480,
              height: '100%',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            <Card
              sx={{
                width: '100%',
                background: 'rgba(255, 255, 255, 0.06)',
                backdropFilter: 'blur(40px)',
                borderRadius: '40px',
                border: '1px solid rgba(255, 255, 255, 0.08)',
                boxShadow: '0 25px 80px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)',
                padding: { xs: 3, sm: 4, md: 5 },
                position: 'relative',
                overflow: 'hidden',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 35px 100px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 0, position: 'relative', zIndex: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <Box
                    sx={{
                      display: { xs: 'flex', md: 'none' },
                      flexDirection: 'column',
                      alignItems: 'center',
                      mb: 3,
                    }}
                  >
                    <Box
                      component="img"
                      src={logo}
                      alt={restaurantName}
                      sx={{
                        width: 80,
                        height: 'auto',
                        mb: 1,
                        padding: '4px',
                        background: 'rgba(200, 200, 200, 0.85)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                      }}
                    />
                  </Box>

                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      background: `linear-gradient(135deg, ${primaryColor}30, ${primaryLight}30)`,
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.1)',
                      boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                      mb: 2,
                    }}
                  >
                    <LockOutlinedIcon sx={{ fontSize: 32, color: whiteColor }} />
                  </Avatar>

                  <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                      fontWeight: 700,
                      color: whiteColor,
                      fontFamily: 'Playfair Display, serif',
                      fontSize: '2rem',
                      letterSpacing: 0.5,
                      textShadow: '0 2px 20px rgba(0,0,0,0.3)',
                    }}
                  >
                    Sign In
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      mb: 3,
                      textAlign: 'center',
                      fontSize: '0.95rem',
                    }}
                  >
                    Enter your credentials to access {restaurantName}
                  </Typography>

                  {/* Error/Success Messages */}
                  {serverError && (
                    <Alert 
                      severity="error" 
                      sx={{ 
                        mb: 2, 
                        width: '100%',
                        borderRadius: 3,
                      }}
                    >
                      <AlertTitle>Login Failed</AlertTitle>
                      {serverError}
                    </Alert>
                  )}

                  {successMessage && (
                    <Alert 
                      severity="success" 
                      sx={{ 
                        mb: 2, 
                        width: '100%',
                        borderRadius: 3,
                      }}
                    >
                      <AlertTitle>Success</AlertTitle>
                      {successMessage}
                    </Alert>
                  )}

                  <Box
                    component="form"
                    noValidate
                    onSubmit={handleSubmit}
                    sx={{ mt: 1, width: '100%' }}
                  >
                    <TextField
                      placeholder="Enter your email address"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      type="email"
                      autoComplete="email"
                      error={!!errors.email}
                      helperText={errors.email}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailOutlinedIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 4,
                          backgroundColor: 'rgba(255,255,255,0.04)',
                          backdropFilter: 'blur(10px)',
                          color: whiteColor,
                          transition: 'all 0.3s ease',
                          padding: '4px 8px',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.06)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            '& fieldset': {
                              borderColor: 'rgba(255,255,255,0.12)',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '& fieldset': {
                              borderColor: primaryColor,
                              borderWidth: 2,
                            },
                          },
                          '& input': {
                            padding: '12px 8px',
                            color: whiteColor,
                            '&::placeholder': {
                              color: 'rgba(255,255,255,0.3)',
                              opacity: 1,
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          display: 'none',
                        },
                        '& .MuiFormHelperText-root': {
                          color: 'rgba(255,255,255,0.5)',
                          marginLeft: '8px',
                        },
                      }}
                    />

                    <TextField
                      placeholder="Enter your password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      variant="outlined"
                      margin="normal"
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      error={!!errors.password}
                      helperText={errors.password}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon sx={{ color: 'rgba(255,255,255,0.3)' }} />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={handleClickShowPassword}
                              edge="end"
                              sx={{ color: 'rgba(255,255,255,0.3)' }}
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 4,
                          backgroundColor: 'rgba(255,255,255,0.04)',
                          backdropFilter: 'blur(10px)',
                          color: whiteColor,
                          transition: 'all 0.3s ease',
                          padding: '4px 8px',
                          '& fieldset': {
                            borderColor: 'rgba(255,255,255,0.06)',
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255,255,255,0.08)',
                            '& fieldset': {
                              borderColor: 'rgba(255,255,255,0.12)',
                            },
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'rgba(255,255,255,0.1)',
                            '& fieldset': {
                              borderColor: primaryColor,
                              borderWidth: 2,
                            },
                          },
                          '& input': {
                            padding: '12px 8px',
                            color: whiteColor,
                            '&::placeholder': {
                              color: 'rgba(255,255,255,0.3)',
                              opacity: 1,
                            },
                          },
                        },
                        '& .MuiInputLabel-root': {
                          display: 'none',
                        },
                        '& .MuiFormHelperText-root': {
                          color: 'rgba(255,255,255,0.5)',
                          marginLeft: '8px',
                        },
                      }}
                    />

                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={isLoading}
                      sx={{
                        mt: 3,
                        py: 1.8,
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '1rem',
                        borderRadius: 4,
                        background: redGradient,
                        color: whiteColor,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: `0 4px 30px ${secondaryColor}30, 0 0 60px ${secondaryColor}10`,
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: '-100%',
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent)',
                          transition: 'all 0.6s ease',
                        },
                        '&:hover': {
                          transform: 'translateY(-3px) scale(1.02)',
                          boxShadow: `0 8px 40px ${secondaryColor}40, 0 0 80px ${secondaryColor}20`,
                          '&::before': {
                            left: '100%',
                          },
                        },
                        '&:active': {
                          transform: 'translateY(-1px) scale(0.98)',
                        },
                        '&.Mui-disabled': {
                          background: 'rgba(198, 40, 40, 0.4)',
                          color: 'rgba(255,255,255,0.3)',
                        },
                      }}
                      endIcon={!isLoading && <KeyboardArrowRightIcon />}
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>

                    <Divider
                      sx={{
                        my: 3,
                        width: '100%',
                        borderColor: 'rgba(255,255,255,0.05)',
                        '&::before, &::after': {
                          borderColor: 'rgba(255,255,255,0.05)',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'rgba(255,255,255,0.2)',
                          px: 2,
                          letterSpacing: 2,
                        }}
                      >
                        {restaurantName.toUpperCase()}
                      </Typography>
                    </Divider>

                    <Box sx={{ textAlign: 'center', mt: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255,255,255,0.2)',
                          fontSize: '0.75rem',
                          letterSpacing: 0.5,
                        }}
                      >
                        {supportPhone} | {supportEmail}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'rgba(255,255,255,0.15)',
                          fontSize: '0.65rem',
                          mt: 0.5,
                        }}
                      >
                        &copy; {new Date().getFullYear()} {restaurantName}. All rights reserved.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Restaurant_LoginPage;